// Progressive enhancement for markdown-embedded videos.
//
// The renderer emits `<div class="video-player"><video controls …></video></div>`.
// Native `controls` is the no-JS fallback; here we strip it and mount a slim
// control bar *below* the frame. Keeping the scrubber/mute/fullscreen chrome
// out of the picture means they never mask subtitles burned into the video.
//
// The one in-frame affordance is intentional: a large center play button (and a
// duration badge) while paused. Without those, a still first frame reads as an
// image — the control bar alone is too easy to miss.

const ICON = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>',
  volume:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z"/></svg>',
  muted:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 10v4h4l5 5V5L7 10H3zm16.5 2 2.3-2.3-1.2-1.2-2.3 2.3-2.3-2.3-1.2 1.2 2.3 2.3-2.3 2.3 1.2 1.2 2.3-2.3 2.3 2.3 1.2-1.2z"/></svg>',
  enterFullscreen:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>',
  exitFullscreen:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>',
} as const

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00'
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m)
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function button(role: string, label: string, icon: string): HTMLButtonElement {
  const el = document.createElement('button')
  el.type = 'button'
  el.className = 'video-player__btn'
  el.dataset.role = role
  el.setAttribute('aria-label', label)
  el.innerHTML = icon
  return el
}

function enhance(container: HTMLElement): void {
  if (container.dataset.enhanced) return
  const video = container.querySelector('video')
  if (!video) return
  container.dataset.enhanced = 'true'

  // Native controls (and any legacy inline click-to-play) would fight the custom
  // bar below, so drop them; the bar + overlay are the sole affordances.
  video.removeAttribute('controls')
  video.removeAttribute('onclick')
  container.tabIndex = 0

  // Frame wraps the video so overlays can sit on the picture without covering
  // the below-frame control bar.
  const frame = document.createElement('div')
  frame.className = 'video-player__frame'
  video.replaceWith(frame)
  frame.appendChild(video)

  const overlayPlay = button('overlay-play', 'Play video', ICON.play)
  overlayPlay.className = 'video-player__overlay-play'
  frame.appendChild(overlayPlay)

  const badge = document.createElement('div')
  badge.className = 'video-player__badge'
  badge.setAttribute('aria-hidden', 'true')
  badge.hidden = true
  frame.appendChild(badge)

  const playBtn = button('play', 'Play', ICON.play)

  const scrubber = document.createElement('div')
  scrubber.className = 'video-player__scrubber'
  scrubber.setAttribute('role', 'slider')
  scrubber.setAttribute('aria-label', 'Seek')
  scrubber.setAttribute('aria-valuemin', '0')
  scrubber.tabIndex = -1
  const buffered = document.createElement('div')
  buffered.className = 'video-player__buffered'
  const progress = document.createElement('div')
  progress.className = 'video-player__progress'
  const handle = document.createElement('div')
  handle.className = 'video-player__handle'
  scrubber.append(buffered, progress, handle)

  const time = document.createElement('div')
  time.className = 'video-player__time'
  const current = document.createElement('span')
  current.textContent = '0:00'
  const duration = document.createElement('span')
  duration.className = 'video-player__duration'
  duration.textContent = '0:00'
  time.append(current, document.createTextNode(' / '), duration)

  const muteBtn = button('mute', 'Mute', ICON.volume)
  const fullscreenBtn = button('fullscreen', 'Fullscreen', ICON.enterFullscreen)

  const bar = document.createElement('div')
  bar.className = 'video-player__controls'
  bar.append(playBtn, scrubber, time, muteBtn, fullscreenBtn)
  container.appendChild(bar)

  // --- play / pause ---------------------------------------------------------
  const togglePlay = () => {
    if (video.paused) video.play().catch(() => {})
    else video.pause()
  }
  playBtn.addEventListener('click', togglePlay)
  overlayPlay.addEventListener('click', togglePlay)
  video.addEventListener('click', togglePlay)
  video.addEventListener('play', () => {
    container.classList.add('is-playing')
    playBtn.innerHTML = ICON.pause
    playBtn.setAttribute('aria-label', 'Pause')
    overlayPlay.setAttribute('aria-hidden', 'true')
    overlayPlay.tabIndex = -1
  })
  video.addEventListener('pause', () => {
    container.classList.remove('is-playing')
    playBtn.innerHTML = ICON.play
    playBtn.setAttribute('aria-label', 'Play')
    overlayPlay.removeAttribute('aria-hidden')
    overlayPlay.tabIndex = 0
  })

  // --- progress -------------------------------------------------------------
  const setProgress = () => {
    const ratio = video.duration ? video.currentTime / video.duration : 0
    const pct = `${(ratio * 100).toFixed(3)}%`
    progress.style.width = pct
    handle.style.left = pct
    current.textContent = formatTime(video.currentTime)
    scrubber.setAttribute('aria-valuenow', String(Math.floor(video.currentTime)))
  }
  const setDuration = () => {
    const formatted = formatTime(video.duration)
    duration.textContent = formatted
    scrubber.setAttribute('aria-valuemax', String(Math.floor(video.duration || 0)))
    if (Number.isFinite(video.duration) && video.duration > 0) {
      badge.textContent = formatted
      badge.hidden = false
    }
    setProgress()
  }
  video.addEventListener('timeupdate', setProgress)
  video.addEventListener('loadedmetadata', setDuration)
  if (video.readyState >= 1) setDuration()

  video.addEventListener('progress', () => {
    if (!video.duration || video.buffered.length === 0) return
    const end = video.buffered.end(video.buffered.length - 1)
    buffered.style.width = `${(end / video.duration) * 100}%`
  })

  // --- scrubbing ------------------------------------------------------------
  const seekToClientX = (clientX: number) => {
    if (!video.duration) return
    const rect = scrubber.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    video.currentTime = ratio * video.duration
    setProgress()
  }
  scrubber.addEventListener('pointerdown', (event) => {
    event.preventDefault()
    scrubber.setPointerCapture(event.pointerId)
    seekToClientX(event.clientX)
    const move = (e: PointerEvent) => seekToClientX(e.clientX)
    const up = () => {
      scrubber.removeEventListener('pointermove', move)
      scrubber.removeEventListener('pointerup', up)
      scrubber.removeEventListener('pointercancel', up)
    }
    scrubber.addEventListener('pointermove', move)
    scrubber.addEventListener('pointerup', up)
    scrubber.addEventListener('pointercancel', up)
  })

  // --- mute -----------------------------------------------------------------
  muteBtn.addEventListener('click', () => {
    video.muted = !video.muted
  })
  const syncMute = () => {
    const off = video.muted || video.volume === 0
    muteBtn.innerHTML = off ? ICON.muted : ICON.volume
    muteBtn.setAttribute('aria-label', off ? 'Unmute' : 'Mute')
    container.classList.toggle('is-muted', off)
  }
  video.addEventListener('volumechange', syncMute)
  syncMute()

  // --- fullscreen -----------------------------------------------------------
  fullscreenBtn.addEventListener('click', () => {
    if (document.fullscreenElement === container) document.exitFullscreen()
    else container.requestFullscreen?.().catch(() => {})
  })
  document.addEventListener('fullscreenchange', () => {
    const active = document.fullscreenElement === container
    container.classList.toggle('is-fullscreen', active)
    fullscreenBtn.innerHTML = active ? ICON.exitFullscreen : ICON.enterFullscreen
    fullscreenBtn.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Fullscreen')
  })

  // --- keyboard -------------------------------------------------------------
  container.addEventListener('keydown', (event) => {
    switch (event.key) {
      case ' ':
      case 'k':
        event.preventDefault()
        togglePlay()
        break
      case 'ArrowLeft':
        event.preventDefault()
        video.currentTime = Math.max(0, video.currentTime - 5)
        break
      case 'ArrowRight':
        event.preventDefault()
        video.currentTime = Math.min(
          video.duration || Number.POSITIVE_INFINITY,
          video.currentTime + 5
        )
        break
      case 'm':
        event.preventDefault()
        video.muted = !video.muted
        break
      case 'f':
        event.preventDefault()
        fullscreenBtn.click()
        break
    }
  })
}

// Wrap a standalone <video> (raw HTML written directly in markdown) in a
// .video-player so it gets the same below-frame bar as renderer-emitted videos.
function wrapStandalone(video: HTMLVideoElement): void {
  const container = document.createElement('div')
  container.className = 'video-player'
  video.replaceWith(container)
  container.appendChild(video)
}

export function initVideoPlayers(root: ParentNode = document): void {
  root.querySelectorAll<HTMLVideoElement>('.blog-post video').forEach((video) => {
    if (!video.closest('.video-player')) wrapStandalone(video)
  })
  root.querySelectorAll<HTMLElement>('.video-player').forEach(enhance)
}
